import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import { Body, Link } from '@grnhse/seedling/lib/birch/typography';
import {
  PrimaryButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { withLocalePrefix } from 'shared/utils/translation';
import CustomColumnSelector from 'talent_matching/components/shared/custom_column_selector';
import BulkAddToJobModal from 'talent_matching/talent_rediscovery/components/bulk_actions/bulk_add_to_job_modal';
import BulkAddToProspectProcessModal from 'talent_matching/talent_rediscovery/components/bulk_actions/bulk_add_to_prospect_process_modal';
import BulkSendEmailModal from 'talent_matching/talent_rediscovery/components/bulk_actions/bulk_send_email_modal';
import AddTagsModal from 'talent_matching/talent_rediscovery/components/bulk_actions/add_tags_modal';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { formatCount } from 'talent_matching/components/header_row/candidate_count_helper';
import { useRediscoveryTabContext } from 'talent_matching/contexts/rediscovery_tab_context';

const tHeader = withLocalePrefix('talent_matching.header_row');
const tJob = withLocalePrefix(
  'talent_matching.rediscovery.bulk_actions.add_to_job'
);
const tProspect = withLocalePrefix(
  'talent_matching.rediscovery.bulk_actions.add_to_prospect_process'
);
const tEmail = withLocalePrefix('talent_matching.bulk_actions.send_email');
const tTags = withLocalePrefix('talent_matching.bulk_actions.add_tags');

type Props = {
  totalCount: number;
  totalSelected: number;
  noneSelected: boolean;
  applicationIds: number[];
  onClear: () => void;
  columnSelectorColumns: { field: string; label: string }[];
  columnVisibilityModel: GridColumnVisibilityModel;
  setColumnVisibilityModel: (model: GridColumnVisibilityModel) => void;
  defaultColumnVisibilityModel?: GridColumnVisibilityModel;
  isLoading?: boolean;
};

const RediscoveryHeaderRow = ({
  totalCount,
  totalSelected,
  noneSelected,
  applicationIds,
  onClear,
  columnSelectorColumns,
  columnVisibilityModel,
  setColumnVisibilityModel,
  defaultColumnVisibilityModel,
  isLoading,
}: Props) => {
  const {
    paths: { emailModalPath },
  } = useRediscoveryTabContext();

  const [addToJobIsOpen, openAddToJobModal, closeAddToJobModal] = useModal();
  const [addToProspectIsOpen, openAddToProspectModal, closeAddToProspectModal] =
    useModal();
  const [sendEmailIsOpen, openSendEmailModal, closeSendEmailModal] = useModal();
  const [addTagsIsOpen, openAddTagsModal, closeAddTagsModal] = useModal();

  const countLabel = noneSelected ? (
    <Body medium>
      {isLoading
        ? tHeader('loading')
        : tHeader('candidates', {
            count: totalCount,
            formattedCount: formatCount(totalCount),
          })}
    </Body>
  ) : (
    <Stack direction="row" spacing={1} alignItems="center">
      <Body medium>
        {tHeader('selected', {
          count: totalSelected,
          formattedCount: formatCount(totalSelected),
        })}
      </Body>
      <Link onClick={onClear}>{tHeader('clear')}</Link>
    </Stack>
  );

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ marginBottom: 2 }}
      >
        {countLabel}

        {noneSelected ? (
          <CustomColumnSelector
            columns={columnSelectorColumns}
            columnVisibilityModel={columnVisibilityModel}
            setColumnVisibilityModel={setColumnVisibilityModel}
            defaultColumnVisibilityModel={defaultColumnVisibilityModel}
          />
        ) : (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <PrimaryButton size="md" onClick={openAddToProspectModal}>
              {tProspect('button')}
            </PrimaryButton>
            <SecondaryButton size="md" onClick={openAddToJobModal}>
              {tJob('button')}
            </SecondaryButton>
            {emailModalPath && (
              <SecondaryButton size="md" onClick={openSendEmailModal}>
                {tEmail('button')}
              </SecondaryButton>
            )}
            <SecondaryButton size="md" onClick={openAddTagsModal}>
              {tTags('button')}
            </SecondaryButton>
          </Stack>
        )}
      </Stack>

      {addToJobIsOpen && (
        <BulkAddToJobModal
          isOpen={addToJobIsOpen}
          closeModal={closeAddToJobModal}
          applicationIds={applicationIds}
          totalSelected={totalSelected}
        />
      )}

      {addToProspectIsOpen && (
        <BulkAddToProspectProcessModal
          isOpen={addToProspectIsOpen}
          closeModal={closeAddToProspectModal}
          applicationIds={applicationIds}
          totalSelected={totalSelected}
        />
      )}

      {sendEmailIsOpen && emailModalPath && (
        <BulkSendEmailModal
          isOpen={sendEmailIsOpen}
          closeModal={closeSendEmailModal}
          applicationIds={applicationIds}
          totalSelected={totalSelected}
        />
      )}

      {addTagsIsOpen && (
        <AddTagsModal
          isOpen={addTagsIsOpen}
          closeModal={closeAddTagsModal}
          applicationIds={applicationIds}
          totalSelected={totalSelected}
        />
      )}
    </>
  );
};

export default RediscoveryHeaderRow;
